import React, { useState } from 'react';
import {
  Box, 
  Button, 
  TextField, 
  Typography, 
  Grid, 
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import api from '../services/api';

const AddOutletPage: React.FC = () => {
  const [sku, setSku] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<any | null>(null);
  const [discountPrice, setDiscountPrice] = useState('');
  const [quantity, setQuantity] = useState(1);
  
  // Enhanced state management
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');
  
  // Calculate savings percentage when both prices are available
  const calculateSavingsPercent = (originalPrice: number, discountPrice: number): number => {
    return Math.round(((originalPrice - discountPrice) / originalPrice) * 100);
  };

  // Show a notification message
  const showMessage = (msg: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setMessage(msg);
    setAlertSeverity(severity);
    setSnackbarOpen(true);
  };

  // Handle SKU search
  const handleSearch = async () => {
    if (!sku.trim()) {
      showMessage('請輸入 SKU 搜尋', 'warning');
      return;
    }

    try {
      setSearchLoading(true);
      const res = await api.post('/api/shopify/search-variant', { sku });
      
      if (!res.data.variants || res.data.variants.length === 0) {
        showMessage('找不到符合的 SKU 變體', 'info');
        setSearchResults([]);
      } else {
        setSearchResults(res.data.variants);
      }
    } catch (err: any) {
      console.error('Search error:', err);
      showMessage(err.response?.data?.message || '搜尋失敗', 'error');
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle adding an item to outlet
  const handleAddOutlet = async () => {
    if (!selectedVariant) {
      showMessage('請選擇產品款式', 'warning');
      return;
    }

    if (!discountPrice || parseFloat(discountPrice) <= 0) {
      showMessage('請輸入有效的特價金額', 'warning');
      return;
    }
    
    // Check if discount price is less than original price
    if (parseFloat(discountPrice) >= parseFloat(selectedVariant.price)) {
      showMessage('特價必須低於原價', 'warning');
      return;
    }

    try {
      setLoading(true);
      
      // Check if item already exists
      const existingResponse = await api.post('/api/shopify/search-variant', { 
        sku: selectedVariant.sku 
      });
      
      const response = await api.post('/api/outlet/add', {
        sku: selectedVariant.sku,
        discount_price: parseFloat(discountPrice),
        available_quantity: quantity
      });
      
      // Reset form on success
      setSku('');
      setSelectedVariant(null);
      setSearchResults([]);
      setDiscountPrice('');
      setQuantity(1);
      
      showMessage(response.data.message || '已成功新增至 Outlet', 'success');
    } catch (err: any) {
      console.error('Add outlet error:', err);
      
      if (err.response?.status === 429) {
        showMessage('API 呼叫頻率限制，請稍候再試', 'error');
      } else {
        showMessage(err.response?.data?.message || '新增失敗', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>新增 Outlet 商品</Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="輸入 SKU 搜尋"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              placeholder="輸入商品 SKU"
              variant="outlined"
            />
          </Grid>
          <Grid item>
            <Button 
              variant="contained" 
              onClick={handleSearch}
              startIcon={searchLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              disabled={searchLoading || !sku.trim()}
            >
              搜尋
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {searchResults.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>請選擇款式 / 尺寸</Typography>
          <Grid container spacing={2}>
            {searchResults.map((variant) => (
              <Grid item key={variant.id} xs={12} sm={6} md={4}>
                <Paper
                  onClick={() => setSelectedVariant(variant)}
                  elevation={selectedVariant?.id === variant.id ? 3 : 1}
                  sx={{ 
                    p: 2, 
                    cursor: 'pointer', 
                    border: selectedVariant?.id === variant.id ? '2px solid #4c5e8a' : '1px solid #e0e0e0',
                    transition: 'all 0.2s',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {variant.image && (
                    <Box sx={{ textAlign: 'center', mb: 1 }}>
                      <img 
                        src={variant.image} 
                        alt={variant.title} 
                        style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }} 
                      />
                    </Box>
                  )}
                  <Typography variant="subtitle1" gutterBottom><strong>{variant.title}</strong></Typography>
                  <Typography variant="body2">{variant.option1} {variant.option2 && `/ ${variant.option2}`}</Typography>
                  <Typography variant="body2">原價: ${parseFloat(variant.price).toFixed(2)}</Typography>
                  <Typography variant="body2">SKU: {variant.sku}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {selectedVariant && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>設定 Outlet 價格與數量</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="原價"
                    type="number"
                    fullWidth
                    value={selectedVariant.price}
                    disabled
                    InputProps={{
                      startAdornment: <Box component="span" sx={{ mr: 1 }}>$</Box>
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="特價"
                    type="number"
                    fullWidth
                    value={discountPrice}
                    onChange={(e) => setDiscountPrice(e.target.value)}
                    InputProps={{
                      startAdornment: <Box component="span" sx={{ mr: 1 }}>$</Box>
                    }}
                    required
                    error={Boolean(discountPrice) && (parseFloat(discountPrice) <= 0 || parseFloat(discountPrice) >= parseFloat(selectedVariant.price))}
                    helperText={
                      discountPrice && parseFloat(discountPrice) >= parseFloat(selectedVariant.price) 
                        ? "特價必須低於原價" 
                        : discountPrice && parseFloat(discountPrice) <= 0
                          ? "價格必須大於零"
                          : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="數量"
                    type="number"
                    fullWidth
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    inputProps={{ min: 1 }}
                    required
                  />
                </Grid>
                
                {discountPrice && parseFloat(discountPrice) > 0 && parseFloat(discountPrice) < parseFloat(selectedVariant.price) && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: '#f5f5f5', mt: 2 }}>
                      <Typography variant="body1">
                        節省金額: ${(parseFloat(selectedVariant.price) - parseFloat(discountPrice)).toFixed(2)} 
                        <Chip 
                          label={`節省 ${calculateSavingsPercent(parseFloat(selectedVariant.price), parseFloat(discountPrice))}%`} 
                          color="error" 
                          size="small" 
                          sx={{ ml: 1, fontWeight: 'bold' }} 
                        />
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                    onClick={handleAddOutlet}
                    disabled={
                      loading || 
                      !discountPrice || 
                      parseFloat(discountPrice) <= 0 ||
                      parseFloat(discountPrice) >= parseFloat(selectedVariant.price)
                    }
                    sx={{ mt: 2 }}
                  >
                    新增至 Outlet
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={alertSeverity}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddOutletPage;